import { useEffect, useState } from "react";
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  serverTimestamp,
} from "firebase/firestore";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import { db, storage } from "../../lib/firebase";

export default function PortfolioPage() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const q = query(collection(db, "portfolio"), orderBy("year", "desc"));
    return onSnapshot(q, (snap) =>
      setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
  }, []);

  async function createCase() {
    await addDoc(collection(db, "portfolio"), {
      title: "Nuevo caso",
      summary: "",
      year: new Date().getFullYear(),
      tags: [],
      createdAt: serverTimestamp(),
    });
  }

  async function save(id, field, value) {
    await updateDoc(doc(db, "portfolio", id), { [field]: value });
  }

  async function setCover(id, file, current) {
    if (!file?.type?.startsWith("image/")) return;
    if (current?.storagePath) {
      try { await deleteObject(ref(storage, current.storagePath)); } catch {}
    }
    const path = `portfolio/${id}/${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
    const task = uploadBytesResumable(ref(storage, path), file);
    task.on("state_changed", null, console.error, async () => {
      const url = await getDownloadURL(task.snapshot.ref);
      await save(id, "cover", { url, storagePath: path });
    });
  }

  async function setPhoto(id, file, current) {
    if (!file?.type?.startsWith("image/")) return;
    if (current?.storagePath) {
      try { await deleteObject(ref(storage, current.storagePath)); } catch {}
    }
    const path = `portfolio/${id}/photo-${Date.now()}-${file.name.replace(/\s+/g, "_")}`;
    const task = uploadBytesResumable(ref(storage, path), file);
    task.on("state_changed", null, console.error, async () => {
      const url = await getDownloadURL(task.snapshot.ref);
      await save(id, "realPhoto", { url, storagePath: path });
    });
  }

  async function removeCase(it) {
    if (!confirm(`Eliminar caso "${it.title}"?`)) return;
    if (it.cover?.storagePath) {
      try { await deleteObject(ref(storage, it.cover.storagePath)); } catch {}
    }
    await deleteDoc(doc(db, "portfolio", it.id));
  }

  return (
    <section style={{ display: "grid", gap: "1.5rem" }}>
      <header>
        <span className="mono-tag">// 02 — Portafolio</span>
        <h1
          style={{
            fontSize: "var(--step-3)",
            fontWeight: 300,
            letterSpacing: "-0.02em",
            marginTop: "0.5rem",
          }}
        >
          Casos del <strong>portafolio</strong>
          <span style={{ color: "var(--color-accent)" }}>.</span>
        </h1>
      </header>

      <button
        onClick={createCase}
        style={{
          padding: "0.7rem 1rem",
          border: "1px dashed var(--color-line)",
          fontFamily: "var(--font-mono)",
          fontSize: "var(--step--1)",
          width: "max-content",
        }}
      >
        + Nuevo caso
      </button>

      <ul style={{ display: "grid", gap: "1rem" }}>
        {items.map((it) => (
          <li
            key={it.id}
            style={{
              display: "grid",
              gridTemplateColumns: "180px 180px 1fr auto",
              gap: "1rem",
              padding: "1rem",
              border: "1px solid var(--color-line)",
              background: "var(--color-bg)",
              alignItems: "start",
            }}
          >
            <label style={{ cursor: "pointer", display: "block" }}>
              {it.cover?.url ? (
                <img
                  src={it.cover.url}
                  alt={it.title}
                  style={{
                    width: "100%",
                    aspectRatio: "16/10",
                    objectFit: "cover",
                    border: "1px solid var(--color-line)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "16/10",
                    background: "var(--color-surface)",
                    border: "1px dashed var(--color-line)",
                    display: "grid",
                    placeItems: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--step--2)",
                    color: "var(--color-ink-soft)",
                  }}
                >
                  + portada
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  e.target.files?.[0] && setCover(it.id, e.target.files[0], it.cover)
                }
              />
            </label>

            <label style={{ cursor: "pointer", display: "block" }}>
              {it.realPhoto?.url ? (
                <img
                  src={it.realPhoto.url}
                  alt="Foto real"
                  style={{
                    width: "100%",
                    aspectRatio: "16/10",
                    objectFit: "cover",
                    border: "1px solid var(--color-line)",
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    aspectRatio: "16/10",
                    background: "var(--color-surface)",
                    border: "1px dashed var(--color-line)",
                    display: "grid",
                    placeItems: "center",
                    fontFamily: "var(--font-mono)",
                    fontSize: "var(--step--2)",
                    color: "var(--color-ink-soft)",
                  }}
                >
                  + foto real
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={(e) =>
                  e.target.files?.[0] && setPhoto(it.id, e.target.files[0], it.realPhoto)
                }
              />
            </label>

            <div style={{ display: "grid", gap: "0.6rem" }}>
              <input
                defaultValue={it.title}
                onBlur={(e) => save(it.id, "title", e.target.value)}
                style={inputStyle}
                placeholder="Título"
              />
              <input
                defaultValue={it.year}
                onBlur={(e) => save(it.id, "year", Number(e.target.value) || it.year)}
                style={inputStyle}
                placeholder="Año"
                type="number"
              />
              <textarea
                rows={3}
                defaultValue={it.summary}
                onBlur={(e) => save(it.id, "summary", e.target.value)}
                style={inputStyle}
                placeholder="Resumen del caso"
              />
            </div>

            <button
              onClick={() => removeCase(it)}
              style={{
                fontFamily: "var(--font-mono)",
                fontSize: "var(--step--1)",
                color: "var(--color-ink-soft)",
                alignSelf: "start",
              }}
            >
              Eliminar
            </button>
          </li>
        ))}
        {items.length === 0 && (
          <li className="mono-tag">// aún no hay casos</li>
        )}
      </ul>
    </section>
  );
}

const inputStyle = {
  border: 0,
  borderBottom: "1px solid var(--color-line)",
  background: "transparent",
  padding: "0.45rem 0",
  fontSize: "var(--step-0)",
  fontFamily: "inherit",
  resize: "vertical",
};
